import type { AssetFieldDefinition } from '@/types/oscar';

/**
 * Renders the category-specific custom fields defined in oscar.AssetFieldDefinition
 * as a grid of inputs, driven by the definitions. Values are held as raw strings
 * by the parent (keyed by FieldKey); use the helpers in `@/lib/assetAttributes`
 * (coerce / buildAttributes / missingRequired) to validate and build the payload.
 *
 * Shared by the asset Create/Edit form and the Intune-orphan "Create asset"
 * dialog so required custom fields can be satisfied in either place.
 */
const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

export function AssetAttributeFields({ defs, values, onChange }: {
  defs: AssetFieldDefinition[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  if (defs.length === 0) return null;
  const set = (key: string, value: string) => onChange({ ...values, [key]: value });
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {defs.map((def) => (
        <label key={def.FieldDefinitionId} className="block text-xs font-semibold text-darkGrey">
          {def.Label}{def.IsRequired ? ' *' : ''}
          {def.DataType === 'Select' ? (
            <select value={values[def.FieldKey] ?? ''} onChange={(e) => set(def.FieldKey, e.target.value)} className={inputCls}>
              <option value="">—</option>
              {(def.Options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : def.DataType === 'Boolean' ? (
            <select value={values[def.FieldKey] ?? ''} onChange={(e) => set(def.FieldKey, e.target.value)} className={inputCls}>
              <option value="">—</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : (
            <input
              type={def.DataType === 'Number' ? 'number' : def.DataType === 'Date' ? 'date' : 'text'}
              value={values[def.FieldKey] ?? ''}
              onChange={(e) => set(def.FieldKey, e.target.value)}
              className={inputCls}
            />
          )}
        </label>
      ))}
    </div>
  );
}
